/**
 * Composing the finished photograph.
 *
 * The working photograph comes out of `capture.ts` with the phone already gone,
 * in camera space. Everything here is framing and finish on top of that —
 * un-mirroring, straightening, the focal plane, the signature — done with
 * canvas compositing rather than per-pixel JavaScript, so toggling any of them
 * is immediate on a two-thousand-pixel photograph.
 */
import { mk, drawCtx, ctxOf } from '../vision/image';
import { coverScale } from '../vision/tilt';
import { personMask } from '../ml/tasks';
import type { CaptureResult } from './capture';

export interface RenderOptions {
  /** Straighten using the room's own lines. */
  autoLevel: boolean;
  /** Un-mirror, so text in the room reads and the face is the one others see. */
  unmirror: boolean;
  /** Throw the room slightly out of focus behind the person. */
  focalPlane: boolean;
  /** Sign the photograph. */
  watermark: boolean;
}

export const DEFAULT_RENDER: RenderOptions = {
  autoLevel: true,
  unmirror: true,
  focalPlane: false,
  watermark: false,
};

/** Blur radius of the out-of-focus room, as a fraction of the composite width. */
const DEFOCUS = 0.014;
/** How softly the focal plane falls off at the edge of the person. */
const FALLOFF_BLUR = 0.008;

function copyOf(source: HTMLCanvasElement): HTMLCanvasElement {
  const out = mk(source.width, source.height);
  drawCtx(out).drawImage(source, 0, 0);
  return out;
}

function flipped(source: HTMLCanvasElement): HTMLCanvasElement {
  const out = mk(source.width, source.height);
  const ctx = drawCtx(out);
  ctx.translate(source.width, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(source, 0, 0);
  return out;
}

function rotated(source: HTMLCanvasElement, degrees: number): HTMLCanvasElement {
  if (!degrees) return source;
  const { width: w, height: h } = source;
  const out = mk(w, h);
  const ctx = drawCtx(out);
  const scale = coverScale(w, h, degrees);
  ctx.translate(w / 2, h / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.scale(scale, scale);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, -w / 2, -h / 2);
  return out;
}

/**
 * Throw the room out of focus behind the person.
 *
 * The mask edge is blurred before it is used, and the result remapped over a
 * narrow band, so the transition reads as a lens rolling off rather than as a
 * cut-out pasted onto a background.
 */
function applyFocalPlane(source: HTMLCanvasElement, mask: Uint8Array): HTMLCanvasElement {
  const { width: w, height: h } = source;

  const soft = mk(w, h);
  const softCtx = ctxOf(soft);
  const img = softCtx.createImageData(w, h);
  for (let i = 0; i < w * h; i++) {
    const v = mask[i];
    img.data[i * 4] = v;
    img.data[i * 4 + 1] = v;
    img.data[i * 4 + 2] = v;
    img.data[i * 4 + 3] = 255;
  }
  softCtx.putImageData(img, 0, 0);

  const feathered = mk(w, h);
  const featherCtx = ctxOf(feathered);
  featherCtx.filter = `blur(${Math.max(2, Math.round(w * FALLOFF_BLUR))}px)`;
  featherCtx.drawImage(soft, 0, 0);
  featherCtx.filter = 'none';

  // Turn the feathered mask into alpha, remapped so the person is fully sharp
  // and the room is fully soft with a short ramp between.
  const read = featherCtx.getImageData(0, 0, w, h);
  for (let i = 0; i < w * h; i++) {
    const a = Math.min(1, Math.max(0, (read.data[i * 4] / 255 - 0.35) / 0.4));
    read.data[i * 4] = 255;
    read.data[i * 4 + 1] = 255;
    read.data[i * 4 + 2] = 255;
    read.data[i * 4 + 3] = Math.round(a * 255);
  }
  featherCtx.putImageData(read, 0, 0);

  const sharp = copyOf(source);
  const sharpCtx = drawCtx(sharp);
  sharpCtx.globalCompositeOperation = 'destination-in';
  sharpCtx.drawImage(feathered, 0, 0);
  sharpCtx.globalCompositeOperation = 'source-over';

  const out = mk(w, h);
  const outCtx = drawCtx(out);
  outCtx.filter = `blur(${Math.max(2, Math.round(w * DEFOCUS))}px)`;
  outCtx.drawImage(source, 0, 0);
  outCtx.filter = 'none';
  outCtx.drawImage(sharp, 0, 0);
  return out;
}

/** A small, quiet signature in the corner. */
function sign(canvas: HTMLCanvasElement): void {
  const ctx = drawCtx(canvas);
  const pad = Math.round(canvas.width * 0.033);
  const size = Math.max(11, Math.round(canvas.width * 0.021));
  ctx.save();
  ctx.font = `500 ${size}px 'IBM Plex Mono', ui-monospace, monospace`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.shadowColor = 'rgba(0,0,0,0.45)';
  ctx.shadowBlur = size * 0.6;
  ctx.fillStyle = 'rgba(255,255,255,0.82)';
  ctx.letterSpacing = `${size * 0.12}px`;
  ctx.fillText('SECOND PERSON', canvas.width - pad, canvas.height - pad);
  ctx.restore();
}

/**
 * Map a point on the framed photograph back to the camera frame it came from:
 * the inverse of un-mirroring and then straightening. Brush strokes are drawn
 * on what people see, and have to land on the pixels underneath.
 */
export function toSource(
  x: number,
  y: number,
  w: number,
  h: number,
  opts: Pick<RenderOptions, 'unmirror' | 'autoLevel'>,
  tiltDegrees: number,
): { x: number; y: number } {
  let px = x;
  let py = y;
  if (opts.autoLevel && tiltDegrees) {
    const rad = (-tiltDegrees * Math.PI) / 180;
    const k = coverScale(w, h, tiltDegrees);
    const dx = (px - w / 2) / k;
    const dy = (py - h / 2) / k;
    px = w / 2 + dx * Math.cos(rad) - dy * Math.sin(rad);
    py = h / 2 + dx * Math.sin(rad) + dy * Math.cos(rad);
  }
  if (opts.unmirror) px = w - px;
  return { x: px, y: py };
}

/**
 * The forward map: a camera-frame pixel to where it lands on the framed
 * photograph. Un-mirroring is a flip; straightening is a rotation about the
 * centre, scaled up just enough to leave no empty corners — exactly what
 * `rotated()` draws. `toSource` is its inverse.
 */
export function toDisplay(
  x: number,
  y: number,
  w: number,
  h: number,
  opts: Pick<RenderOptions, 'unmirror' | 'autoLevel'>,
  tiltDegrees: number,
): { x: number; y: number } {
  let px = opts.unmirror ? w - x : x;
  let py = y;
  if (opts.autoLevel && tiltDegrees) {
    const rad = (tiltDegrees * Math.PI) / 180;
    const k = coverScale(w, h, tiltDegrees);
    const dx = px - w / 2;
    const dy = py - h / 2;
    px = w / 2 + k * (dx * Math.cos(rad) - dy * Math.sin(rad));
    py = h / 2 + k * (dx * Math.sin(rad) + dy * Math.cos(rad));
  }
  return { x: px, y: py };
}

/**
 * Holds one capture and turns options into pictures.
 *
 * The person mask for the focal plane is measured once per framing and kept,
 * because depth is the one finish that needs a model, and toggling the others
 * should not pay for it again.
 */
export class Composer {
  #capture: CaptureResult;
  #mask: Uint8Array | null = null;
  #maskKey: string | null = null;

  constructor(capture: CaptureResult) {
    this.#capture = capture;
  }

  get capture(): CaptureResult {
    return this.#capture;
  }

  #frame(source: HTMLCanvasElement, opts: RenderOptions): HTMLCanvasElement {
    const facing = opts.unmirror ? flipped(source) : copyOf(source);
    return opts.autoLevel ? rotated(facing, this.#capture.tiltDegrees) : facing;
  }

  /** What the person mask was measured against; a change invalidates it. */
  #framingKey(opts: RenderOptions): string {
    return `${this.#capture.strokes}:${opts.unmirror ? 'u' : 'm'}:${opts.autoLevel ? this.#capture.tiltDegrees.toFixed(2) : '0'}`;
  }

  /** The photograph as the camera took it, framed the same way, for the wipe. */
  before(opts: RenderOptions): HTMLCanvasElement {
    return this.#frame(this.#capture.original, opts);
  }

  /** The finished photograph. */
  async render(opts: RenderOptions): Promise<HTMLCanvasElement> {
    let out = this.#frame(this.#capture.erased, opts);

    if (opts.focalPlane) {
      const key = this.#framingKey(opts);
      if (key !== this.#maskKey) {
        this.#maskKey = key;
        const soft = await personMask(out);
        this.#mask = soft ? Uint8Array.from(soft, (v) => Math.round(v * 255)) : null;
      }
      if (this.#mask) out = applyFocalPlane(out, this.#mask);
    }

    if (opts.watermark) {
      out = copyOf(out);
      sign(out);
    }
    return out;
  }

  /** Forget the person mask, so the next depth pass measures it again. */
  invalidateMask(): void {
    this.#mask = null;
    this.#maskKey = null;
  }
}
